import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

interface SubMenuItem {
  label: string;
  link: string;
}

interface MenuGroup {
  label: string;
  icon: string;
  open: boolean;
  children: SubMenuItem[];
}

@Component({
  selector: 'app-side-bar',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.scss']
})
export class SideBarComponent {

  constructor(private router: Router) {
    this.syncSidebarWidthVar();
  }

  isOpen = true; // collapsed by default
  searchTerm = '';

  menuGroups: MenuGroup[] = [
    {
      label: 'Dashboards',
      icon: 'fa-solid fa-gauge-high',
      open: false,
      children: [
        { label: 'Overview Dashboard', link: '/app/dashboard' },
        { label: 'Live Map View', link: '/app/dashboard' },
        { label: 'KPIs (Trips, Students, Vehicles)', link: '/app/dashboard' }
      ]
    },
    {
      label: 'Master Configuration',
      icon: 'fa-solid fa-gear',
      open: false,
      children: [
        { label: 'Vehicles', link: '/app/masters/vehicles' },
        { label: 'Drivers', link: '/app/masters/drivers' },
        { label: 'Shifts', link: '/app/masters/shifts' },
        { label: 'Routes', link: '/app/masters/routes' },
        { label: 'Account Holder', link: '/app/masters/accountHolders' },
        { label: 'Pre-Route Checklist', link: '/app/masters/preRouteChecklist' }
      ]
    },
    {
      label: 'Assignment Management',
      icon: 'fa-solid fa-diagram-project',
      open: false,
      children: [
        { label: 'Pickup/Drop Configuration', link: '/app/masters/stopConfiguration' },
        { label: 'Route Assignment', link: '/app/masters/assignments' }
      ]
    },
    {
      label: 'Reports',
      icon: 'fa-solid fa-chart-line',
      open: false,
      children: [
        { label: 'Company Details', link: '/app/masters/companyDetails' },
        { label: 'Pre-Route Checklist Reports', link: '/app/masters/preRouteChecklistReports' },
        { label: 'Passenger Attendance Reports', link: '/app/masters/attendanceReports' }
      ]
    }
  ];

  get isSearching(): boolean {
    return this.searchTerm.trim().length > 0;
  }

  // Filter groups + their children by the search term.
  // A group shows if its own label matches (then all children show) or any child matches.
  get filteredGroups(): MenuGroup[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.menuGroups;
    }
    return this.menuGroups
      .map(group => {
        const groupMatches = group.label.toLowerCase().includes(term);
        const children = groupMatches
          ? group.children
          : group.children.filter(c => c.label.toLowerCase().includes(term));
        return groupMatches || children.length
          ? { ...group, children }
          : null;
      })
      .filter((g): g is MenuGroup => g !== null);
  }

  trackByLabel(_index: number, item: { label: string }): string {
    return item.label;
  }

  clearSearch() {
    this.searchTerm = '';
  }

  toggleSidebar() {
    this.isOpen = !this.isOpen;
    this.syncSidebarWidthVar();
  }

  /**
   * Publishes the sidebar's current rendered width (w-72/w-20) as a CSS variable
   * on the document root, so fixed-position overlays in other components (e.g.
   * modals) can offset themselves past the sidebar without hardcoding its width.
   */
  private syncSidebarWidthVar(): void {
    document.documentElement.style.setProperty(
      '--sidebar-width',
      this.isOpen ? '288px' : '80px'
    );
  }

  navigate(path: string) {
    this.router.navigate([path]);
  }
}
